// src/components/games/crossroads/GameInvitationMessage.tsx
import { logger } from '../../../utils/logger';
// Component for rendering Crossroads TCG game invitations in chat

import React, { useState } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonText,
  IonSpinner,
  IonBadge,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  useIonToast,
} from '@ionic/react';
import {
  gameControllerOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
} from 'ionicons/icons';
import { createMultiplayerGame } from '../../../services/crossroadsMultiplayerManager';
import { STARTER_DECKS } from './cardData';
import { useHistory } from 'react-router-dom';

interface GameInvitationData {
  type: 'CROSSROADS_GAME_INVITATION';
  gameType: string;
  challengerDeck?: string; // Deck selected by challenger
  timestamp: string;
}

// Available decks with display info
const DECK_OPTIONS = [
  { key: 'fire', name: 'Fire', emoji: '🔥', commander: 'Dice and Autism' },
  { key: 'water', name: 'Water', emoji: '💧', commander: 'Nic' },
  { key: 'earth', name: 'Earth', emoji: '🌱', commander: 'Carma' },
  { key: 'dark', name: 'Dark', emoji: '🌑', commander: 'Neal' },
  { key: 'light', name: 'Light', emoji: '☀️', commander: 'Brandy' },
  { key: 'fae', name: 'Fae', emoji: '✨', commander: 'Alli' },
  { key: 'wind', name: 'Wind', emoji: '💨', commander: 'Doug' },
];

interface GameInvitationMessageProps {
  invitationData: GameInvitationData;
  senderId: string;
  senderName: string;
  isOwnMessage: boolean;
  matchId: string;
}

export const GameInvitationMessage: React.FC<GameInvitationMessageProps> = ({
  invitationData,
  senderId,
  senderName,
  isOwnMessage,
  matchId,
}) => {
  const [presentToast] = useIonToast();
  const [accepting, setAccepting] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState<string>('water');
  const history = useHistory();

  const challengerDeck = invitationData.challengerDeck || 'fire';
  const challengerDeckInfo = DECK_OPTIONS.find(d => d.key === challengerDeck);

  const handleAccept = async () => {
    setAccepting(true);

    try {
      // Get current user
      const { data: { user } } = await (await import('../../../services/supabase')).supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Get the selected decks
      const challengerDeckDef = STARTER_DECKS[challengerDeck as keyof typeof STARTER_DECKS] || STARTER_DECKS.fire;
      const accepterDeckDef = STARTER_DECKS[selectedDeck as keyof typeof STARTER_DECKS] || STARTER_DECKS.water;

      // Create multiplayer game using our new manager
      const result = await createMultiplayerGame(
        senderId,           // Player 0 (challenger)
        user.id,            // Player 1 (accepter)
        challengerDeckDef,  // Challenger's deck
        accepterDeckDef,    // Accepter's deck
        senderName,         // Challenger name
        'You'               // Accepter name
      );

      if (!result.success || !result.gameId) {
        throw new Error(result.error || 'Failed to create game');
      }

      setAccepted(true);
      presentToast({
        message: '🎮 Game created! Let the battle begin!',
        duration: 2000,
        position: 'top',
        color: 'success',
      });

      // Redirect to multiplayer hub with game state
      setTimeout(() => {
        // Navigate to multiplayer with game ID in state
        history.push('/games/crossroads/multiplayer', {
          autoJoinGameId: result.gameId,
          playerIndex: 1
        });
      }, 1000);

    } catch (error) {
      logger.error('Error accepting game invitation:', error);
      presentToast({
        message: 'Failed to start game. Please try again.',
        duration: 2000,
        position: 'top',
        color: 'danger',
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    setDeclined(true);
    presentToast({
      message: 'Challenge declined',
      duration: 1500,
      position: 'top',
    });
  };

  return (
    <IonCard
      className="game-invitation-card"
      style={{
        margin: '8px 0',
        border: '2px solid var(--ion-color-primary)',
        background: 'linear-gradient(135deg, rgba(var(--ion-color-primary-rgb), 0.1), rgba(var(--ion-color-secondary-rgb), 0.05))',
      }}
    >
      <IonCardHeader>
        <IonCardTitle>
          <IonIcon icon={gameControllerOutline} style={{ marginRight: '8px' }} />
          Crossroads TCG Challenge
        </IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <div style={{ marginBottom: '12px' }}>
          <IonText>
            {isOwnMessage ? (
              <p><strong>You</strong> challenged with your deck!</p>
            ) : (
              <p><strong>{senderName}</strong> challenges you to a duel!</p>
            )}
          </IonText>

          {/* Show challenger's deck */}
          {challengerDeckInfo && (
            <div style={{ marginTop: '8px' }}>
              <IonBadge color="primary">
                Challenger: {challengerDeckInfo.emoji} {challengerDeckInfo.name}
              </IonBadge>
            </div>
          )}
        </div>

        {/* Deck selection for recipient */}
        {!isOwnMessage && !accepted && !declined && (
          <IonItem style={{ marginBottom: '12px' }}>
            <IonLabel>Choose Your Deck</IonLabel>
            <IonSelect
              value={selectedDeck}
              onIonChange={(e) => setSelectedDeck(e.detail.value)}
              interface="popover"
            >
              {DECK_OPTIONS.map((deck) => (
                <IonSelectOption key={deck.key} value={deck.key}>
                  {deck.emoji} {deck.name}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
        )}

        {!isOwnMessage && !accepted && !declined && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <IonButton
              expand="block"
              color="success"
              onClick={handleAccept}
              disabled={accepting}
              style={{ flex: 1 }}
            >
              {accepting ? (
                <>
                  <IonSpinner name="crescent" style={{ marginRight: '8px' }} />
                  Creating Game...
                </>
              ) : (
                <>
                  <IonIcon icon={checkmarkCircleOutline} slot="start" />
                  Accept Challenge
                </>
              )}
            </IonButton>
            <IonButton
              expand="block"
              color="danger"
              fill="outline"
              onClick={handleDecline}
              disabled={accepting}
              style={{ flex: 1 }}
            >
              <IonIcon icon={closeCircleOutline} slot="start" />
              Decline
            </IonButton>
          </div>
        )}

        {accepted && (
          <IonText color="success">
            <p><strong>✓ Challenge Accepted!</strong> Starting game...</p>
          </IonText>
        )}

        {declined && (
          <IonText color="medium">
            <p>Challenge declined</p>
          </IonText>
        )}

        {isOwnMessage && (
          <IonText color="medium">
            <p style={{ fontSize: '0.9em', marginTop: '8px' }}>
              Waiting for response...
            </p>
          </IonText>
        )}
      </IonCardContent>
    </IonCard>
  );
};

/**
 * Parse message content to detect game invitations
 */
export function parseGameInvitation(messageContent: string): GameInvitationData | null {
  const inviteMatch = messageContent.match(/\[GAME_INVITE:(.*?)\]/);
  if (!inviteMatch) return null;

  try {
    const data = JSON.parse(inviteMatch[1]);
    if (data.type === 'CROSSROADS_GAME_INVITATION') {
      return data;
    }
  } catch (error) {
    logger.error('Error parsing game invitation:', error);
  }

  return null;
}

/**
 * Remove the invitation marker from message content for display
 */
export function stripInvitationMarker(messageContent: string): string {
  return messageContent.replace(/\[GAME_INVITE:.*?\]\n\n/, '');
}
